import React, { Component } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { Link } from "react-router-dom"
import { get } from "lodash"

import Loader from "components/Loader/Loader.js"
import ContentBox from "components/ContentBox/ContentBox.js"
import PageNotFound from "containers/PageErrors/PageNotFound"
import PageChannelView from "./PageChannelView.js"

import getChannel, { getChannelReplaySection } from "actions/channels"

import selectChannel from "selectors/channels"
import { selectReferencesModel } from "selectors/references"
import { updateMeta, restoreDefaultMeta } from "helpers/meta"
import formatterHelper from "helpers/formatter"
import routeHelpers from "helpers/route"
import pictureHelper from "helpers/picture"
import { getAssetURL } from "helpers/static"

import styles from "./index.css"
import consts from "consts"
import { LOCALE } from "consts/locale"

const ADULT_SWIM_CHANNELS = ["236"]

@connect((state, ownProps) => ({
  channels: selectChannel(state),
  params: ownProps.match.params,
  query: routeHelpers.queryToObject(ownProps.location.search),
  references: selectReferencesModel(state),
  seo: state.seo,
}))
export default class PageChannel extends Component {
  static propTypes = {
    channels: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    downloadAdGroup: PropTypes.string,
    downloadCampaign: PropTypes.string,
    params: PropTypes.object.isRequired,
    query: PropTypes.object.isRequired,
    references: PropTypes.object.isRequired,
    locale: PropTypes.string.isRequired,
    location: PropTypes.object.isRequired,
    seo: PropTypes.object.isRequired,
  }

  static cache = consts.cache.defaultCacheMaxAge

  static mandatoryParams = ["id"]

  static getChannelId(props) {
    if (props.location.pathname === "/deeplink") {
      return props.query.id
    }
    return props.params.channelId
  }

  static isMango(props) {
    return PageChannel.getChannelId(props) === "330"
  }

  static fetchData(dispatch, state, renderProps) {
    const channelId = PageChannel.getChannelId(renderProps)

    const requests = [dispatch(getChannel(channelId))]

    if (routeHelpers.isChannelReplayPage(renderProps.location.pathname)) {
      requests.push(dispatch(getChannelReplaySection(channelId)))
    }

    return requests
  }

  static getMeta(state, renderProps) {
    const references = selectReferencesModel(state)
    const channelId = PageChannel.getChannelId(renderProps)
    const channel = references.getChannelById(channelId)
    const isMango = PageChannel.isMango(renderProps)

    if (!channel || !channel.isValid()) {
      return
    }

    const isReplay = routeHelpers.isChannelReplayPage(renderProps.location.pathname)

    const title = isMango
      ? "Mango AVOD, l'offre streaming gratuit par Molotov"
      : `Regarder ${channel.getTitle()} ${isReplay ? "en replay" : "en direct"} - live streaming sur Molotov.tv`

    const canonicalMeta = {
      canonical: `${consts.externalLink.landing}${isReplay ? channel.getReplayUrl() : channel.getUrl()}`,
    }

    if (ADULT_SWIM_CHANNELS.includes(channel.data.id)) {
      canonicalMeta.canonical = `${consts.externalLink.landing}${consts.routes.adultSwim}`
      canonicalMeta.__noCanonicalRedirect = true
    }

    let programImage

    if (renderProps.channels) {
      const program = isReplay ? renderProps.channels.mainReplayProgram : renderProps.channels.mainDirectProgram
      if (!program) {
        return
      }
      programImage = pictureHelper.replaceProtocol(program.isAvailable() ? program.getImageUrl("large") : program.getImageUrl("large", "poster"))
    } else {
      programImage = getAssetURL("image.pageHome.ogImage")
    }

    const og = {
      type: "website",
      image: programImage,
      imageAlt: `${channel.getTitle()} ${isReplay ? "en replay" : "en direct et replay"}`,
      url: `${consts.externalLink.landing}${isReplay ? channel.getReplayUrl() : channel.getUrl()}`,
      title: `${channel.getTitle()} ${isReplay ? "en replay" : "en direct"} - Regardez la télévision sur Internet - Molotov.tv`,
      description: `Téléchargez gratuitement l'application Molotov TV afin de regarder tous les programmes ${channel.getTitle()} en direct${
        isReplay ? " et en replay." : "."
      }`,
    }

    return {
      title,
      description: isMango
        ? "Découvrez Mango, le service de vidéo à la demande gratuit par Molotov et accédez à des centaines de programmes en streaming gratuit"
        : `Téléchargez gratuitement l'application Molotov TV afin de regarder tous les programmes de ${channel.getTitle()} ${
            isReplay ? "en replay" : "en direct"
          }. Vos programmes préférés en live streaming.`,
      ...canonicalMeta,
      og,
    }
  }

  static getHttpStatus(state, renderProps) {
    const channelId = PageChannel.getChannelId(renderProps)
    const references = selectReferencesModel(state)
    const channel = references.getChannelById(channelId)

    if (!channel || !channel.isValid()) {
      return { code: 410 }
    }

    const isReplay = routeHelpers.isChannelReplayPage(renderProps.location.pathname)

    // redirect to live page if the channel does not have replays
    if (isReplay && !channel.hasReplay()) {
      return {
        code: 301,
        redirect: channel.getUrl(),
      }
    }

    return { code: 200 }
  }

  setMeta(props) {
    updateMeta(this.constructor.getMeta(props, props))
  }

  componentDidMount() {
    if (!global.serverRendered) {
      this.constructor.fetchData(this.props.dispatch, this.props, this.props)
    }
  }

  componentDidUpdate(prevProps) {
    const prevChannelId = this.constructor.getChannelId(prevProps)
    const channelId = this.constructor.getChannelId(this.props)

    if (
      prevChannelId !== channelId ||
      routeHelpers.isChannelReplayPage(prevProps.location.pathname) !== routeHelpers.isChannelReplayPage(this.props.location.pathname)
    ) {
      this.constructor.fetchData(this.props.dispatch, this.props, this.props)
    }

    this.setMeta(this.props, this.props)
  }

  componentWillUnmount() {
    restoreDefaultMeta()
  }

  render() {
    const { channels, references, downloadCampaign, downloadAdGroup, location, seo } = this.props
    const isMango = PageChannel.isMango(this.props)

    if (channels.pending) {
      return <Loader centerInParent={true} />
    }

    if (!channels.getMapItemDirect) {
      return (
        <ContentBox className={styles.errorBox}>
          <h2 className={styles.errorTitle}>La chaine que vous recherchez semble introuvable.</h2>
          <Link className={styles.errorLink} to={formatterHelper.basic(consts.routes.seoHome, { locale: LOCALE })}>
            Retourner au programme TV
          </Link>
        </ContentBox>
      )
    }

    const isReplay = routeHelpers.isChannelReplayPage(location.pathname)

    const channel = references.getChannelById(this.constructor.getChannelId(this.props))

    if (!channel || !channel.isValid()) {
      return <PageNotFound />
    }

    let description = channels.directDescription
    let program = channels.mainDirectProgram
    let sections = channels.getMapItemDirect && channels.getMapItemDirect()

    if (isReplay) {
      description = channels.replayDescription
      program = channels.mainReplayProgram
      sections = channels.getMapItemReplay && channels.getMapItemReplay()
    }

    const isCharged = !channel.isAvailableInFreeBundles(get(seo, "settings.tv_bundles_visible", []))

    return (
      <PageChannelView
        channel={channel}
        description={description}
        downloadAdGroup={downloadAdGroup}
        downloadCampaign={downloadCampaign}
        isCharged={isCharged}
        isMango={isMango}
        isReplay={isReplay}
        location={location}
        program={program}
        references={references}
        sections={sections}
      />
    )
  }
}
